/**
 * Audit Detail Page
 *
 * - Shows form_data + metrics + status
 * - Polls /status every 2s while generating, with backoff after 30s
 * - Download button (when ready)
 * - Reassign button (super_admin only, when property_id is null)
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '../../api';
import '../components/audit/audit.css';
import { StatusBadge, CapacityBadge } from '../components/audit/AuditMetricsBadge';
import { useAuth } from '../../context/AuthContext';

export default function AuditDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showReassign, setShowReassign] = useState(false);

  const pollRef = useRef(null);
  const startedRef = useRef(Date.now());

  const refresh = useCallback(async () => {
    try {
      const r = await api.getAudit(id);
      setAudit(r.audit);
      setLoading(false);
      return r.audit;
    } catch (e) {
      setError(e.message);
      setLoading(false);
      return null;
    }
  }, [id]);

  const downloadWorkbook = useCallback(async () => {
    if (!audit) return;
    try {
      await api.downloadAuditWorkbook(audit);
    } catch (e) {
      setError(e.message);
    }
  }, [audit]);

  const deleteAudit = useCallback(async () => {
    if (!audit) return;
    const propLabel = audit.property_name || audit.property_label || 'this audit';
    const ok = window.confirm(
      `Permanently delete the audit for "${propLabel}"?\n\n` +
      `• Removes the workbook and every uploaded source file from disk.\n` +
      `• Cannot be undone.`
    );
    if (!ok) return;
    try {
      await api.deleteAudit(audit.id);
      navigate('/sales/audits', { replace: true });
    } catch (e) {
      setError(e.message);
    }
  }, [audit, navigate]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Poll while generating with progressive backoff.
  // Only re-runs when status flips (not on every metrics update) to avoid
  // tearing down and re-arming the timer on each tick.
  const status = audit?.status;
  useEffect(() => {
    if (status !== 'generating') {
      if (pollRef.current) clearTimeout(pollRef.current);
      return;
    }
    const elapsed = Date.now() - startedRef.current;
    const delay = elapsed < 30_000 ? 2000 : elapsed < 90_000 ? 5000 : 10_000;
    pollRef.current = setTimeout(refresh, delay);
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [status, refresh]);

  if (loading) return <div className="page audit-detail"><div className="audit-loading">Loading audit…</div></div>;
  if (error) return <div className="page audit-detail"><div className="audit-error">{error}</div></div>;
  if (!audit) return null;

  const m = audit.metrics || {};
  const f = audit.form_data || {};
  const isSuperAdmin = user?.permission_tier === 'super_admin';
  const canReassign = isSuperAdmin && !audit.property_id;

  return (
    <div className="page audit-detail">
      <header className="audit-detail-header">
        <div>
          <h1>{audit.property_name || audit.property_label || 'Audit'}</h1>
          <p className="audit-detail-meta">
            {audit.property_address && <span>{audit.property_address} · </span>}
            <span>Audited {audit.created_at?.slice(0, 10)}</span>
            <span> · By {audit.created_by_name || '—'}</span>
            <span> · </span><StatusBadge status={audit.status} />
            {!audit.property_id && <span className="audit-other-tag">"Other" — not in master list</span>}
          </p>
        </div>
        <div className="audit-detail-actions">
          {audit.status === 'ready' && (
            <button type="button" onClick={downloadWorkbook} className="audit-btn primary">
              Download Workbook
            </button>
          )}
          {canReassign && (
            <button type="button" className="audit-btn ghost" onClick={() => setShowReassign(true)}>
              Link to property…
            </button>
          )}
          {isSuperAdmin && (
            <button
              type="button"
              onClick={deleteAudit}
              style={{
                padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                border: '1.5px solid #fecaca', background: '#fff', color: '#b91c1c',
                cursor: 'pointer',
              }}
              title="Permanently delete this audit (super_admin only)"
            >
              Delete
            </button>
          )}
          <Link to="/sales/audits" className="audit-link-btn">← All audits</Link>
        </div>
      </header>

      {audit.status === 'generating' && (
        <div className="audit-generating">
          Generating workbook… this typically takes 20-60 seconds.
        </div>
      )}

      {audit.status === 'failed' && (
        <div className="audit-error audit-failed">
          <strong>Generation failed.</strong>
          {audit.status_error && <pre>{audit.status_error}</pre>}
        </div>
      )}

      {audit.status === 'ready' && (
        <div className="audit-detail-metrics">
          <div className="audit-metric-card">
            <span className="metric-label">Total Units</span>
            <strong>{m.total_units ?? '—'}</strong>
          </div>
          <div className="audit-metric-card">
            <span className="metric-label">Occupied</span>
            <strong>{m.occupied_units ?? '—'}</strong>
          </div>
          <div className="audit-metric-card audit-metric-tow-risk">
            <span className="metric-label">TOW RISK</span>
            <strong>{m.tow_risk_count ?? '—'}</strong>
          </div>
          <div className="audit-metric-card">
            <span className="metric-label">Compliance</span>
            <strong>{m.compliance_rate != null ? `${m.compliance_rate}%` : '—'}</strong>
          </div>
          <div className="audit-metric-card">
            <span className="metric-label">Total Vehicles</span>
            <strong>{m.total_vehicles ?? '—'}</strong>
          </div>
          {m.units_without_myvip != null && (
            <div className="audit-metric-card audit-metric-tow-risk">
              <span className="metric-label">Units w/o MyVIP</span>
              <strong>{m.units_without_myvip}</strong>
            </div>
          )}
          <div className="audit-metric-card">
            <span className="metric-label">Name Discrepancies</span>
            <strong>{m.name_discrepancy_count ?? '—'}</strong>
          </div>
        </div>
      )}

      {audit.status === 'ready' && (
        <div className="audit-capacity-row">
          <CapacityBadge label="Cars Registered" value={m.cars_registered ?? 0} capacity={f.total_parking_spaces ?? 0} />
          <CapacityBadge label="Reserved Used" value={m.reserved_registered ?? 0} capacity={f.reserved ?? 0} />
        </div>
      )}

      {showReassign && (
        <ReassignModal auditId={audit.id} onClose={() => setShowReassign(false)} onDone={refresh} />
      )}
    </div>
  );
}

function ReassignModal({ auditId, onClose, onDone }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (search.length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.searchAuditProperties(search).then(r => setResults(r.properties || [])).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const select = async (p) => {
    setSaving(true);
    setError('');
    try {
      await api.reassignAudit(auditId, p.uuid);
      await onDone();
      onClose();
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  return (
    <div className="audit-modal-backdrop" onClick={onClose}>
      <div className="audit-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Link to master property</h3>
        <p className="audit-modal-help">
          Select the property record this audit should be linked to. This action is logged.
        </p>
        <input
          type="text"
          placeholder="Search properties…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
        <ul className="audit-typeahead-results">
          {results.map(p => (
            <li key={p.uuid} onClick={() => select(p)}>
              <strong>{p.name}</strong>
              {p.address && <small> — {p.address}</small>}
            </li>
          ))}
        </ul>
        {error && <div className="audit-error">{error}</div>}
        <div className="audit-modal-actions">
          <button type="button" className="audit-btn ghost" onClick={onClose} disabled={saving}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
